import React from "react";
import { motion } from "motion/react";
import { ArrowRight, Github, Linkedin, ExternalLink } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import heroImg from "../../assets/1bf2cb9872382b575472820fcc33f22f25acabd8.png";

export function Hero() {
  return (
    <section id="home" className="pt-32 pb-16 md:pt-48 md:pb-32 max-w-7xl mx-auto px-6 overflow-hidden">
      <div className="grid lg:grid-cols-12 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="w-12 h-[1px] bg-brand-primary" />
            <span className="text-xs font-black uppercase tracking-[0.4em] text-brand-primary">
              Freelance & Full-stack Expert
            </span>
          </div>
          
          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black mb-8 leading-[0.9] tracking-tighter uppercase italic">
            Ergys <br />
            <span className="bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-accent bg-clip-text text-transparent animate-gradient-x">Onuzi</span>
          </h1>
          
          <div className="flex flex-col md:flex-row gap-8 items-start mb-12">
            <p className="text-muted-foreground text-lg md:text-xl max-w-md leading-relaxed font-medium">
              Engineering high-performance, <span className="text-foreground">database-driven</span> solutions with a focus on business automation and clean UX.
            </p>
            <div className="hidden md:block w-[1px] h-20 bg-border" />
            <div className="flex flex-col gap-1">
              <span className="text-4xl font-black text-foreground italic tracking-tighter">6+</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Years Industry <br />Experience</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-6 items-center">
            <a 
              href="#contact" 
              className="group relative bg-foreground text-background px-10 py-5 rounded-full font-black uppercase tracking-widest text-sm flex items-center gap-3 hover:bg-brand-primary hover:text-white transition-all duration-500 shadow-2xl shadow-foreground/10 active:scale-95 overflow-hidden"
            >
              <span className="relative z-10">Start a Project</span>
              <ArrowRight size={20} className="relative z-10 group-hover:translate-x-2 transition-transform" />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a 
              href="https://ergys.dev" 
              target="_blank" 
              rel="noreferrer"
              className="text-sm font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all flex items-center gap-3 group"
            >
              Portfolio <ExternalLink size={18} className="group-hover:rotate-12 transition-transform" />
            </a>
          </div>

          <div className="mt-20 flex items-center gap-10">
            <div className="flex gap-6 text-muted-foreground">
              <a 
                href="https://github.com/ErgysOnuzi" 
                target="_blank" 
                rel="noreferrer" 
                className="hover:text-foreground transition-all transform hover:-translate-y-1"
                aria-label="Visit Ergys Onuzi on GitHub"
              >
                <Github size={24} aria-hidden="true" />
              </a>
              <a 
                href="https://al.linkedin.com/in/ergys-onuzi-3131aa193" 
                target="_blank" 
                rel="noreferrer" 
                className="hover:text-foreground transition-all transform hover:-translate-y-1"
                aria-label="Visit Ergys Onuzi on LinkedIn"
              >
                <Linkedin size={24} aria-hidden="true" />
              </a>
            </div>
            <div className="h-[1px] flex-grow bg-border" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="lg:col-span-5 relative group"
        >
          <div className="absolute -top-10 -left-10 w-32 h-32 border-l-2 border-t-2 border-brand-primary/30 rounded-tl-[3rem] group-hover:scale-110 transition-transform duration-700" />
          <div className="absolute -bottom-10 -right-10 w-32 h-32 border-r-2 border-b-2 border-brand-accent/30 rounded-br-[3rem] group-hover:scale-110 transition-transform duration-700" />
          
          <div className="relative rounded-[3rem] overflow-hidden aspect-[4/5] bg-muted p-2 shadow-[0_0_50px_rgba(139,92,246,0.15)] group-hover:shadow-[0_0_80px_rgba(139,92,246,0.3)] transition-all duration-700">
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary/20 via-transparent to-brand-accent/20 z-10 mix-blend-overlay group-hover:opacity-50 transition-opacity" />
            <div className="w-full h-full bg-background rounded-[2.5rem] overflow-hidden relative">
              <ImageWithFallback 
                src={heroImg} 
                alt="Ergys Onuzi" 
                className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-105"
              />
            </div>
          </div>
          
          <div className="absolute top-1/2 -right-12 -translate-y-1/2 bg-background/80 backdrop-blur-xl border border-border p-6 rounded-[2rem] shadow-2xl hidden xl:block animate-bounce-slow">
            <div className="flex flex-col gap-4">
              <div className="w-8 h-8 rounded-full bg-brand-primary/20 flex items-center justify-center text-brand-primary font-black">JS</div>
              <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center text-brand-accent font-black">TS</div>
              <div className="w-8 h-8 rounded-full bg-brand-secondary/20 flex items-center justify-center text-brand-secondary font-black">NX</div>
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500 font-black">PH</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}